import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock,
  ListChecks,
  MessageSquare,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InstructionTrigger } from '@/features/instructions/InstructionDrawer';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useFinance } from '@/contexts/FinanceContext';
import { useUnreadMessageCount } from '@/features/messages/useMessages';
import { TaskCard } from '@/features/tasks/TaskCard';
import { TaskModal } from '@/features/tasks/TaskModal';
import { useMyTasks } from '@/features/tasks/useTasks';
import { bucketTasks } from '@/features/tasks/taskBuckets';
import { BUCKET_LABELS, type TaskWithClient } from '@/features/tasks/types';

function greeting(name: string | null | undefined): string {
  const hour = new Date().getHours();
  const first = name?.split(' ')[0] ?? 'Consultor';
  if (hour < 12) return `Bom dia, ${first} 👋`;
  if (hour < 18) return `Boa tarde, ${first} 👋`;
  return `Boa noite, ${first} 👋`;
}

export function MyDayPage() {
  const { profile } = useAuth();
  const { clientId } = useCurrentClient();
  const { data: tasks = [], isLoading } = useMyTasks();
  const [createOpen, setCreateOpen] = useState(false);

  const buckets = useMemo(() => bucketTasks(tasks), [tasks]);

  // Cards de contexto (só com cliente selecionado)
  const finance = useFinance();
  const awaitingApprovals = clientId
    ? finance.transactions.filter((t) => t.approvalStatus === 'awaiting').length
    : 0;
  const { data: unreadCount = 0 } = useUnreadMessageCount();

  const overdueCount = buckets.overdue.length;
  const todayCount = buckets.today.length;
  const next48Count = buckets.next_48h.length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-brand text-2xl font-bold text-foreground">
              {greeting(profile?.name)}
            </h1>
            <InstructionTrigger contextKey="meu-dia" variant="ghost" label="" iconSize={16} />
          </div>
          <p className="text-sm text-muted-foreground">
            {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Nova tarefa
        </Button>
      </div>

      {/* Resumo numérico no topo */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className={overdueCount > 0 ? 'border-expense/30 bg-expense/5' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle
                className={`h-5 w-5 ${overdueCount > 0 ? 'text-expense' : 'text-muted-foreground'}`}
              />
              <div>
                <p className="text-xs text-muted-foreground">Atrasadas</p>
                <p
                  className={`text-xl font-bold ${overdueCount > 0 ? 'text-expense' : 'text-foreground'}`}
                >
                  {overdueCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Hoje</p>
                <p className="text-xl font-bold text-foreground">{todayCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Próximas 48h</p>
                <p className="text-xl font-bold text-foreground">{next48Count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-income" />
              <div>
                <p className="text-xs text-muted-foreground">Concluídas hoje</p>
                <p className="text-xl font-bold text-foreground">
                  {buckets.recentlyCompleted.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        {/* Coluna principal — buckets de tarefas */}
        <div className="space-y-5">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
              ))}
            </div>
          ) : tasks.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <ListChecks className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p className="font-medium">Nenhuma tarefa pendente</p>
                <p className="mt-1 text-sm">Comece criando sua primeira tarefa do dia.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus size={14} className="mr-1.5" />
                  Nova tarefa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {buckets.overdue.length > 0 && (
                <BucketSection
                  title={BUCKET_LABELS.overdue}
                  tone="expense"
                  count={buckets.overdue.length}
                  tasks={buckets.overdue}
                />
              )}
              {buckets.today.length > 0 && (
                <BucketSection
                  title={BUCKET_LABELS.today}
                  tone="primary"
                  count={buckets.today.length}
                  tasks={buckets.today}
                />
              )}
              {buckets.next_48h.length > 0 && (
                <BucketSection
                  title={BUCKET_LABELS.next_48h}
                  tone="muted"
                  count={buckets.next_48h.length}
                  tasks={buckets.next_48h}
                />
              )}
              {buckets.no_due_date.length > 0 && (
                <BucketSection
                  title={BUCKET_LABELS.no_due_date}
                  tone="muted"
                  count={buckets.no_due_date.length}
                  tasks={buckets.no_due_date}
                />
              )}
              {buckets.later.length > 0 && (
                <BucketSection
                  title={BUCKET_LABELS.later}
                  tone="muted"
                  count={buckets.later.length}
                  tasks={buckets.later}
                />
              )}
              {buckets.recentlyCompleted.length > 0 && (
                <BucketSection
                  title="Concluídas hoje"
                  tone="income"
                  count={buckets.recentlyCompleted.length}
                  tasks={buckets.recentlyCompleted}
                  collapsedByDefault
                />
              )}
            </>
          )}
        </div>

        {/* Sidebar — atalhos contextuais (quando há cliente selecionado) */}
        <div className="space-y-3">
          {clientId ? (
            <>
              {awaitingApprovals > 0 && (
                <Link to="/app/transacoes">
                  <Card className="border-amber-200 bg-amber-50 transition-shadow hover:shadow-sm">
                    <CardContent className="flex items-center gap-3 p-4">
                      <ShieldCheck className="h-6 w-6 text-amber-600" />
                      <div>
                        <p className="text-sm font-semibold text-amber-900">
                          {awaitingApprovals} aprovação(ões) pendente(s)
                        </p>
                        <p className="text-xs text-amber-700">Cliente atual</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )}
              {unreadCount > 0 && (
                <Link to="/app/mensagens">
                  <Card className="transition-shadow hover:shadow-sm">
                    <CardContent className="flex items-center gap-3 p-4">
                      <MessageSquare className="h-6 w-6 text-primary" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {unreadCount} mensagem(ns) não lida(s)
                        </p>
                        <p className="text-xs text-muted-foreground">Cliente atual</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )}
              {awaitingApprovals === 0 && unreadCount === 0 && (
                <Card>
                  <CardContent className="p-4 text-center text-xs text-muted-foreground">
                    Sem aprovações ou mensagens pendentes no cliente atual.
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="space-y-2 p-4 text-center text-xs text-muted-foreground">
                <p>Selecione um cliente no topo para ver atalhos de aprovações e mensagens.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <TaskModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

interface BucketSectionProps {
  title: string;
  count: number;
  tasks: TaskWithClient[];
  tone: 'primary' | 'expense' | 'income' | 'muted';
  collapsedByDefault?: boolean;
}

function BucketSection({ title, count, tasks, tone, collapsedByDefault }: BucketSectionProps) {
  const [collapsed, setCollapsed] = useState(!!collapsedByDefault);
  const toneClasses = {
    primary: 'text-primary',
    expense: 'text-expense',
    income: 'text-income',
    muted: 'text-muted-foreground',
  } as const;
  return (
    <div>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="mb-2 flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-80"
      >
        <span className={toneClasses[tone]}>{title}</span>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-normal text-muted-foreground">
          {count}
        </span>
        <span className="text-xs text-muted-foreground">{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && (
        <div className="space-y-1.5">
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} />
          ))}
        </div>
      )}
    </div>
  );
}
